/**
 * "Open in…" — the top-bar control that opens the page you are on somewhere
 * else: a new window, or another tab.
 *
 * There is deliberately no "open in new browser tab": the main window has its
 * own tab strip with a "+" (MainWindowTabs.tsx), so a tabbed experience never
 * needs a second browser tab. "Add as a tab" therefore adds a tab to THIS
 * window's strip when it has one, and otherwise falls back to the tabbed view
 * (a tabbed pop-out window).
 *
 * Mounted in the app shell's top chrome (the V2 sidebar header on WeaveIn, the
 * pillar bar on Triveny). Renders nothing inside a pop-out, where the tab
 * strip's own "+" and "pop out" actions do this job.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, ExternalLink, Plus, SquareStack } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasInAppTabHandler, isInAnyPopout, openAppPopout, type AppTabSpec, type OpenMode } from '@/lib/popout';
import { config } from '@/src/config';

interface OpenInMenuProps {
  /** The page to open — read at click time so it reflects where the user is now. */
  getSpec: () => AppTabSpec | null;
  /** Trigger styling; the menu itself is the same everywhere. */
  className?: string;
  /** Icon size in px. */
  iconSize?: number;
  /** Overridable for tests. */
  isDesktop?: boolean;
}

/**
 * Rendered inside the menu content, which mounts when the menu opens — so it
 * reads whether this window has its own tab strip at that moment.
 */
function AddAsTabItem({ onSelect }: { onSelect: () => void }) {
  const { t } = useTranslation();
  const here = hasInAppTabHandler();
  return (
    <DropdownMenuItem onSelect={onSelect} className="gap-2" data-testid="open-in-add">
      {here ? <Plus className="h-4 w-4" /> : <SquareStack className="h-4 w-4" />}
      {here ? t('mainTabs.add_here', 'Open in a new tab here') : t('appPopout.add_to_tab_view', 'Add to tab view')}
    </DropdownMenuItem>
  );
}

export function OpenInMenu({ getSpec, className = '', iconSize = 14, isDesktop = config.isDesktop }: OpenInMenuProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(null), 6000);
    return () => window.clearTimeout(id);
  }, [error]);

  if (isInAnyPopout()) return null;

  const open = async (mode: OpenMode) => {
    setError(null);
    const spec = getSpec();
    if (!spec) return;
    const result = await openAppPopout({ tabs: [spec] }, mode, { isDesktop });
    if (!result.ok) {
      setError(
        result.error === 'popup blocked'
          ? t('appPopout.popup_blocked', 'Your browser blocked the new window. Allow pop-ups for this site and try again.')
          : t('appPopout.open_failed', 'Could not open a new window.'),
      );
    }
  };

  const label = t('appPopout.open_in', 'Open this page in a new tab or window');

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

  return (
    <div className="relative inline-flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={
              className ||
              'inline-flex h-7 w-7 items-center justify-center rounded-[var(--v2-r-sm)] text-ink-400 hover:bg-paper-2 hover:text-ink'
            }
            aria-label={label}
            title={label}
            data-testid="open-in-trigger"
          >
            <ExternalLink size={iconSize} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <AddAsTabItem onSelect={() => void open('add-to-tabs')} />
          <DropdownMenuItem onSelect={() => void open('window')} className="gap-2" data-testid="open-in-window">
            <AppWindow className="h-4 w-4" />
            {t('appPopout.open_new_window', 'Open in new window')}
            {isDesktop && <DropdownMenuShortcut>{isMac ? '⇧⌘N' : 'Ctrl+Shift+N'}</DropdownMenuShortcut>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {error && (
        <div
          role="alert"
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 shadow dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default OpenInMenu;
